import { Line } from "react-chartjs-2";
import { useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import CircleIcon from "@mui/icons-material/Circle";

import zoomPlugin from "chartjs-plugin-zoom";
import { round } from "src/app/utils/helper";
import { Grid, Typography } from "@mui/material";

type ObjectDataSet = {
  title: string;
  data: any[];
  label: string;
  borderColor: string;
  pointBackgroundColor: string;
  backgroundColor: string;
  fill: boolean;
  yAxisID: string;
  hidden: boolean;
  minVal: any;
  maxValue: any;
  avgValue: any;
};

const engineHealthStatusZonesPlugin = {
  id: "engine_health_status_zones",
  beforeDraw(chart: any, _args: any, options: any) {
    if (!options?.enabled || !chart.chartArea) return;

    const { chartArea, ctx } = chart;
    const gradient = ctx.createLinearGradient(
      0,
      chartArea.bottom,
      0,
      chartArea.top
    );
    gradient.addColorStop(1, "#A6E58B");
    gradient.addColorStop(0.62, "#A6E58B");
    gradient.addColorStop(0.58, "#E6CB6E");
    gradient.addColorStop(0.38, "#E6CB6E");
    gradient.addColorStop(0.3, "#EE9A50");
    gradient.addColorStop(0.22, "#F0522E");
    gradient.addColorStop(0, "#F0522E");
    ctx.fillStyle = gradient;
    ctx.fillRect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );

    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "600 12px Poppins, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Alert Zone",
      chartArea.left + chartWidth / 2,
      chartArea.top + chartHeight * 0.58
    );
    ctx.fillText(
      "Alarm Zone",
      chartArea.left + chartWidth / 2,
      chartArea.top + chartHeight * 0.82
    );
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
  engineHealthStatusZonesPlugin
);

const zoomOptions = {
  pan: {
    enabled: true,
    mode: "x" as "x",
  },
  zoom: {
    wheel: {
      enabled: true,
    },
    pinch: {
      enabled: true,
    },
    mode: "x" as "x",
  },
};

export default function LineGradientTrends({
  labels,
  dataPoints,
  maxRpm,
  selectedValue,
  yLabel,
}: any) {
  const chartRef = useRef<any>(null);
  const [graphData, setGraphData] = useState<any>({ labels: [], datasets: [] });
  const isEngineHealthSelected = selectedValue.includes("Engine Health");
  const hasSpeedSeries = graphData.datasets.some(
    (dataSet: ObjectDataSet) => dataSet.yAxisID === "y1"
  );
  const isEngineHealthColorEnabled =
    typeof window !== "undefined" && window.sessionStorage
      ? (sessionStorage.getItem("engine_health_color") ??
          localStorage.getItem("engine_health_color")) === "true"
      : false;

  const handlegraphData = (labels: [], dataSets: any, selectedValue: []) => {
    const grData = dataSets
      .filter((x: ObjectDataSet) =>
        selectedValue.some((val: any) => val === x.title)
      )
      .map((dataSet: ObjectDataSet) => ({ ...dataSet }));
    setGraphData({ labels: labels, datasets: grData });
  };

  useEffect(() => {
    handlegraphData(labels, dataPoints, selectedValue);
  }, [labels, dataPoints, selectedValue]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as "index",
      intersect: false,
    },
    stacked: false,
    plugins: {
      engine_health_status_zones: {
        enabled: isEngineHealthSelected && isEngineHealthColorEnabled,
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset?.label || "";
            const value = Number(context.parsed?.y);

            if (label === "Twist Angle" && Number.isFinite(value)) {
              return `${label}: ${value.toFixed(4)}`;
            }

            return `${label}: ${context.parsed?.y ?? ""}`;
          },
        },
      },
      zoom: zoomOptions,
    },
    animation: {
      duration: 0,
    },
    scales: {
      x: {
        ticks: {
          font: {
            weight: "bold",
            size: 14,
            whiteSpace: "normal",
          },
          maxRotation: 0,
          minRotation: 0,
          maxTicksLimit: 7,
        },
        title: {
          text: "Time (Local Time)",
          display: true,
          font: {
            size: 14,
            weight: "bold",
          },
        },
      },
      y: {
        beginAtZero: true,
        type: "linear" as const,
        display: true,
        position: "left" as const,
        max: isEngineHealthSelected ? 110 : undefined,
        // max: 110,
        font: {
          family: 'Poppins,Helvetica,"sans-serif',
          size: 12,
        },
        ticks: {
          font: {
            weight: "bold",
            size: 14,
            whiteSpace: "normal",
          },
          // callback: (value: any) => {
          //   return parseInt(value);
          // },
        },
        title: {
          text: yLabel,
          display: true,
          font: {
            size: 14,
            weight: "bold",
          },
        },
      },
      y1: {
        beginAtZero: true,
        type: "linear" as const,
        display: hasSpeedSeries,
        max: round(maxRpm),
        position: "right" as const,
        ticks: {
          font: {
            weight: "bold",
            size: 14,
            whiteSpace: "normal",
          },
          callback: (value: any) => {
            return parseInt(value);
          },
        },
        title: {
          text: "Speed",
          display: true,
          font: {
            size: 14,
            weight: "bold",
          },
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };
  return (
    <>
      <div style={{ width: "80vw", height: "70vh" }}>
        {graphData.datasets && (
          <Line ref={chartRef} options={options} data={graphData} />
        )}
      </div>
      <div
        style={{
          marginBottom: "10px",
        }}
      >
        {graphData.datasets.map((val: ObjectDataSet) => (
          <Grid
            key={val?.title}
            spacing={2}
            container
            sx={{ width: "80vw", marginBottom: "10px" }}
          >
            <Grid item lg={4}>
              <Typography
                component="span"
                textAlign={"left"}
                alignItems="center"
                display={"flex"}
                sx={{
                  cursor: "pointer",
                  textDecoration: val.hidden ? "line-through" : "none",
                }}
                onClick={() => {
                  let data = graphData.datasets.map((v: any) => {
                    if (val?.title === v.title) {
                      v.hidden = !v.hidden;
                    }
                    return v;
                  });
                  setGraphData({ labels: labels, datasets: data });
                }}
              >
                <CircleIcon sx={{ color: val.borderColor, mr: 1 }} />
                {val?.title}
              </Typography>
            </Grid>
            <Grid item lg={3}>
              <Typography
                component="span"
                textAlign={"left"}
                alignItems="center"
                display={"flex"}
              >
                Min. {val?.minVal}
              </Typography>
            </Grid>
            <Grid item lg={3}>
              <Typography
                component="span"
                textAlign={"left"}
                alignItems="center"
                display={"flex"}
              >
                Max. {val?.maxValue}
              </Typography>
            </Grid>
            <Grid item lg={2}>
              <Typography
                component="span"
                textAlign={"left"}
                alignItems="center"
                display={"flex"}
              >
                Avg. {val?.avgValue}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </div>
    </>
  );
}
